import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import 'moment/locale/ko';
import { CalendarToday } from '@mui/icons-material';
import { Popover, Box, Button } from '@mui/material';
import { DayPickerRangeController } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { Row } from 'saladsoft-primitive';
import { debounce } from 'lodash';

moment.locale('ko');

const DateDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #333;
  padding: 4px 8px;
`;

const DateSeparator = styled.div`
  margin: 0 10px;
`;

export const DateRangePicker = ({
  value,
  onChange,
  style,
}: {
  value?: [string | null, string | null];
  onChange?: (date: [string | null, string | null]) => void;
  style?: React.CSSProperties;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);
  const [focusedInput, setFocusedInput] = useState<'startDate' | 'endDate' | null>(null);

  // debounce된 상태 업데이트 함수
  const debouncedSetDates = useMemo(
    () =>
      debounce((newValue) => {
        if (newValue) {
          if (newValue[0]) {
            setStartDate(moment(newValue[0]));
          } else {
            setStartDate(null);
          }

          if (newValue[1]) {
            setEndDate(moment(newValue[1]));
          } else {
            setEndDate(null);
          }
        }
      }, 300), // 300ms 딜레이
    []
  );

  useEffect(() => {
    debouncedSetDates(value);

    // cleanup
    return () => {
      debouncedSetDates.cancel();
    };
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);

    // 이미 날짜 범위가 완전히 선택된 경우에만 초기화
    // 즉, startDate와 endDate가 모두 있을 때만 초기화
    if (startDate && endDate) {
      // 기존 선택을 유지하고 팝업만 열기
      setFocusedInput('startDate');
    } else {
      // 아직 범위 선택이 완료되지 않은 경우 기존 상태 유지
      setFocusedInput(startDate ? 'endDate' : 'startDate');
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDatesChange = ({
    startDate,
    endDate,
  }: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
  }) => {
    setStartDate(startDate);
    setEndDate(endDate);

    // if (onChange) {
    //   onChange([
    //     startDate ? startDate.format('YYYY-MM-DD') : null,
    //     endDate ? endDate.format('YYYY-MM-DD') : null,
    //   ]);
    // }
  };

  const onClickApply = (startDate: moment.Moment | null, endDate: moment.Moment | null) => {
    handleClose();
    handleDatesChange({
      startDate: startDate,
      endDate: endDate,
    });

    onChange?.([
      startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate ? endDate.format('YYYY-MM-DD') : null,
    ]);
  };

  const formatDate = (date: moment.Moment | null) => {
    return date ? date.format('YYYY-MM-DD') : '날짜 선택';
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (value) {
      setStartDate(moment(value[0]));
      setEndDate(moment(value[1]));
    }
  }, [value]);

  return (
    <div style={style}>
      <DateDisplay onClick={handleClick}>
        <DateText>{formatDate(startDate)}</DateText>
        <DateSeparator>~</DateSeparator>
        <DateText>{formatDate(endDate)}</DateText>
        <CalendarToday fontSize="small" />
      </DateDisplay>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box p={2}>
          <DayPickerRangeController
            startDate={startDate}
            endDate={endDate}
            onDatesChange={handleDatesChange}
            focusedInput={focusedInput || 'startDate'}
            onFocusChange={(focusedInput) => setFocusedInput(focusedInput || 'startDate')}
            numberOfMonths={2}
            // isOutsideRange={(day) => day.isBefore(moment(), 'day')}
            hideKeyboardShortcutsPanel
            noBorder
            initialVisibleMonth={() => moment()}
          />
        </Box>
        <Row justifyContent="flex-end">
          <Button onClick={handleClose}>닫기</Button>
          <Button onClick={() => {
            onClickApply(startDate, endDate);
          }}>적용</Button>
        </Row>
      </Popover>
    </div>
  );
};
